import { AcknowledgementDebtForm } from '../../types/operations';
import { mapReceivableToPayload, buildInstallmentPayload, mapDisbursementToPayload } from '../mappers/dataMappers';
import { sanitizeFileName } from '../helpers/operationHelpers';
import { EMPTY_BILLING_CONFIG } from '../../components/form/BillingConfigSection';
import { formatDateFromBRToISO } from '../../../../../utils/DateUtils';

interface AcknowledgementDebtPayloadParams {
    form: AcknowledgementDebtForm;
    operationType: string;
    creditOperationId?: string;
    selectedDestinatario: string;
    lastInstallmentDueDate?: string;
    participants: unknown[];
    artifactFiles: File[];
    contractFiles: File[];
    existingArtifactFiles: string[];
    removedArtifactFiles: string[];
    existingContractFile: string;
    removedContractFile: boolean;
}

export const buildAcknowledgementDebtPayload = ({
    form,
    operationType,
    creditOperationId,
    selectedDestinatario,
    lastInstallmentDueDate,
    participants,
    artifactFiles,
    contractFiles,
    existingArtifactFiles,
    removedArtifactFiles,
    existingContractFile,
    removedContractFile,
}: AcknowledgementDebtPayloadParams) => {
    const dueDateValue = lastInstallmentDueDate || form.expiration_date || null;
    const hasRecipient = selectedDestinatario && selectedDestinatario !== '00000000-0000-0000-0000-000000000000';
    const assets = form.collaterals.assets;

    const receivablesPayload = {
        items: (assets.receivables?.items || []).map(mapReceivableToPayload),
        total: assets.receivables?.total || 0,
        quantity: assets.receivables?.quantity || 0,
        spreadsheet_processed: assets.receivables?.spreadsheet_processed || false,
        overcollateral: assets.receivables?.overcollateral ?? null,
    };

    const contractGuaranteeItems = assets.contracts?.items || [];
    const contractsPayload = {
        items: contractGuaranteeItems.map(item => ({
            collateral_id: item.collateral_id,
            contract_number: item.contract_number || '',
            contract_value: item.contract_value || 0,
            anticipated_value: item.anticipated_value || 0,
            file_name: item.file_name ? sanitizeFileName(item.file_name) : '',
            description: item.description || '',
            drawee_person_id: item.drawee_person_id || '',
            drawee_document_type: item.drawee_document_type || '',
            drawee_document: item.drawee_document || '',
            drawee_name: item.drawee_name || '',
            drawee_city: item.drawee_city || '',
            drawee_state: item.drawee_state || '',
            installments: (item.installments || []).map(inst => ({
                installment_id: inst.installment_id,
                contract_installment: inst.contract_installment || '',
                due_date: inst.due_date ? formatDateFromBRToISO(inst.due_date) : '',
                installment_maturity_value: inst.installment_maturity_value || 0,
            })),
            installments_quantity: item.installments?.length || 0,
            installments_total: (item.installments || []).reduce((sum, inst) => sum + (inst.installment_maturity_value || 0), 0),
        })),
        total: contractGuaranteeItems.reduce((sum, item) => sum + (item.contract_value || 0), 0),
        quantity: contractGuaranteeItems.length,
    };

    const inventoryPayload = {
        items: assets.inventory?.items || [],
        total: assets.inventory?.total || 0,
        quantity: assets.inventory?.quantity || 0,
    };

    const machineryPayload = {
        items: assets.machinery?.items || [],
        total: assets.machinery?.total || 0,
        quantity: assets.machinery?.quantity || 0,
    };

    const realEstatePayload = {
        items: assets.real_estate?.items || [],
        total: assets.real_estate?.total || 0,
        quantity: assets.real_estate?.quantity || 0,
    };

    const securitiesPayload = {
        items: assets.securities?.items || [],
        total: assets.securities?.total || 0,
        quantity: assets.securities?.quantity || 0,
    };

    const totalValue = receivablesPayload.total + contractsPayload.total + inventoryPayload.total + machineryPayload.total + realEstatePayload.total + securitiesPayload.total;
    const totalQuantity = receivablesPayload.quantity + contractsPayload.quantity + inventoryPayload.quantity + machineryPayload.quantity + realEstatePayload.quantity + securitiesPayload.quantity;

    return {
        type: operationType || "ACKNOWLEDGEMENT_OF_DEBT",
        ...(hasRecipient && { assignor: "00000000-0000-0000-0000-000000000000", recipient: selectedDestinatario }),
        credit_operation_id: creditOperationId,
        operation_number: form.operation_number || "",
        originator: form.originator || "",
        operation_type: "ACKNOWLEDGEMENT_OF_DEBT",
        operation_schema_id: 3,
        approved_on: null,
        emission_date: form.emission_date ? form.emission_date.split('/').reverse().join('-') : null,
        approved_value: null,
        current_balance: null,
        debt_balance_id: null,
        nominal_value: form.total_amount || 0,
        loan_amount: form.loan_amount || 0,
        due_date: dueDateValue,
        expiration_date: form.expiration_date || null,
        participants,
        operation_dictionary: {
            ...(hasRecipient && { assignor: "00000000-0000-0000-0000-000000000000", recipient: selectedDestinatario }),
            emission_date: form.emission_date ? form.emission_date.split('/').reverse().join('-') : "",
            approved_on: form.approved_on ? form.approved_on.split('/').reverse().join('-') : "",
            guarantee_value: form.guarantee_value,
            rebate_amount: form.rebate_amount || 0,
            fees: {
                items: form.fees.items.map(fee => ({
                    rate: fee.rate || 0,
                    amount: fee.amount || 0,
                    form: fee.form || "",
                    name: fee.name || "",
                    interest_period: fee.interest_period || ""
                })),
                quantity: form.fees.items.length
            },
            collaterals: {
                total_value: totalValue,
                total_quantity: totalQuantity,
                assets: {
                    receivables: receivablesPayload,
                    contracts: contractsPayload,
                    inventory: inventoryPayload,
                    machinery: machineryPayload,
                    real_estate: realEstatePayload,
                    securities: securitiesPayload,
                }
            },
            disbursement: {
                disbursements: form.disbursement.disbursements.map(mapDisbursementToPayload),
                totalAmount: form.disbursement.totalAmount || 0
            },
            installment: buildInstallmentPayload(form.installments),
            operation_number: form.operation_number || "",
            external_link: form.external_link || "",
            operation_uf: form.operation_uf || 0,
            operation_city: form.operation_city || "",
            credit_product_type_name: form.credit_product_type_name || "",
            receiving_account_name: form.receiving_account_name || "",
            original_contract_discharged: form.original_contract_discharged || [],
            product: form.crop_type.name || "",
            main_risk: { uuid: form.main_risk.uuid || "", name: form.main_risk.name || "" },
            credit_product_type: "ACKNOWLEDGEMENT_OF_DEBT",
            is_settled: false,
            artifacts: [{ list: "DOCUMENTOS", files: artifactFiles.map(file => file.name) }],
            description_product: { id: form.crop_type.id || "", name: form.crop_type.name || "" },
            base_calculation: form.base_calculation ? {
                tir_annual: form.base_calculation.tir_annual,
                tir_monthly: form.base_calculation.tir_monthly,
                spread_annual: form.base_calculation.spread_annual,
                vpl: form.base_calculation.vpl,
                vpl_margin: form.base_calculation.vpl_margin,
                duration_days: form.base_calculation.duration_days
            } : undefined,
            billing_config: form.billing_config || EMPTY_BILLING_CONFIG,
            assignor_billing_address: form.assignor_billing_address || undefined,
            files: {
                contract_file: contractFiles.length > 0
                    ? `${form.sequencial_number}${sanitizeFileName(contractFiles[0].name.substring(contractFiles[0].name.lastIndexOf('.')))}`
                    : (removedContractFile ? '' : existingContractFile),
                artefacts: [
                    ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                    ...artifactFiles.map(file => sanitizeFileName(file.name))
                ]
            }
        }
    };
};
